import { useEffect, useRef, useState } from 'preact/hooks';
import type { RollingCounterWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import { useLeafMeta } from '../LeafMetaContext';
import { formatNumber } from '../../format/leafValue';
import './RollingCounterRenderer.css';

interface Props {
  widget: RollingCounterWidget;
  source: IPointBindingSource;
}

/**
 * Rolling-digits counter for energy, hours, cycles. Interpolates between
 * value updates using rAF so the digits scroll smoothly instead of jumping.
 * A jump bigger than `jumpThreshold` (default 1000) hard-sets — otherwise
 * a counter reset would spin backwards through every intermediate value
 * (concept §3 rule).
 */
export function RollingCounterRenderer({ widget, source }: Props) {
  const { value, release } = pointSignal(source, widget.valuePoint);
  useEffect(() => release, [release]);
  const meta = useLeafMeta(widget.valuePoint);

  const [displayValue, setDisplayValue] = useState<number | null>(null);
  const animRef = useRef<{ from: number; to: number; startAt: number; duration: number } | null>(null);
  const rafRef = useRef<number | null>(null);

  const current = value.value;
  const stale = isDegraded(current);
  const target = typeof current?.value === 'number' && Number.isFinite(current.value) ? current.value : null;

  useEffect(() => {
    if (target === null) return;
    const previous = displayValue;
    const jumpThreshold = widget.jumpThreshold ?? 1000;
    if (previous === null || Math.abs(target - previous) >= jumpThreshold) {
      setDisplayValue(target);
      return;
    }
    if (previous === target) return;

    // 700 ms transition — long enough to see, short enough to still feel live.
    animRef.current = { from: previous, to: target, startAt: performance.now(), duration: 700 };
    const step = (now: number) => {
      const a = animRef.current;
      if (!a) return;
      const t = Math.min(1, (now - a.startAt) / a.duration);
      const eased = easeOutCubic(t);
      const next = a.from + (a.to - a.from) * eased;
      setDisplayValue(next);
      if (t < 1) {
        rafRef.current = window.requestAnimationFrame(step);
      } else {
        animRef.current = null;
        rafRef.current = null;
      }
    };
    if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
    rafRef.current = window.requestAnimationFrame(step);

    return () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const decimals = widget.decimals ?? 0;
  // The unit belongs to the leaf; the widget may override it.
  const unit = widget.unit ?? meta?.unit;
  const digitWidth = widget.digitWidth ?? 6;

  const formatted = displayValue != null ? formatNumber(displayValue, decimals) : '—';
  const padded = displayValue != null
    ? formatted.padStart(digitWidth + (decimals > 0 ? decimals + 1 : 0), ' ') // figure-space
    : formatted;

  return (
    <div class={`hv-rolling${stale ? ' hv-rolling--stale' : ''}`}>
      {widget.label && <div class="hv-rolling__label">{widget.label}</div>}
      <div class="hv-rolling__row">
        <span class="hv-rolling__digits" style={{ minWidth: `${digitWidth * 0.65 + 1}rem` }}>
          {padded}
        </span>
        {unit && <span class="hv-rolling__unit">{unit}</span>}
      </div>
    </div>
  );
}

function easeOutCubic(t: number): number {
  const p = 1 - t;
  return 1 - p * p * p;
}
